import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  CloudUpload,
} from '@mui/icons-material';

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  filename: string;
  thumbnail?: string;
  isPrimary: boolean;
  caption?: string;
}

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  media: MediaItem[];
  category: 'architectural' | 'mechanical' | 'artistic' | 'functional';
  featured: boolean;
  order: number;
  createdAt: string;
}

interface PortfolioManagementProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

const PortfolioManagement: React.FC<PortfolioManagementProps> = ({ onNotification }) => {
  const theme = useTheme();
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
  const [previewMedia, setPreviewMedia] = useState<MediaItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState<string | null>(null);

  // Load portfolio items on mount
  useEffect(() => {
    const loadPortfolioItems = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.PORTFOLIO, {
          credentials: 'include', // Include session cookie
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setPortfolioItems(data.items || []);
          }
        }
      } catch (error) {
        console.error('Error loading portfolio items:', error);
      }
    };
    loadPortfolioItems();
  }, []);

  const handleAddItem = () => {
    const newItem: PortfolioItem = {
      id: `new-${Date.now()}`, // Prefix with 'new-' to identify new items
      title: '',
      description: '',
      media: [],
      category: 'architectural',
      featured: false,
      order: portfolioItems.length + 1,
      createdAt: new Date().toISOString().split('T')[0],
    };
    setEditingItem(newItem);
  };

  const handleEditItem = (item: PortfolioItem) => {
    setEditingItem({ ...item });
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;

    if (!editingItem.title || !editingItem.description) {
      onNotification('Please fill in title and description', 'error');
      return;
    }

    try {
      const itemToSave = editingItem.id.startsWith('new-') 
        ? { ...editingItem, id: Date.now().toString() }
        : editingItem;

      const response = await fetch(API_ENDPOINTS.PORTFOLIO, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include session cookie
        body: JSON.stringify(itemToSave),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Reload items from server
          const reloadResponse = await fetch(API_ENDPOINTS.PORTFOLIO, {
            credentials: 'include',
          });
          if (reloadResponse.ok) {
            const reloadData = await reloadResponse.json();
            if (reloadData.success) {
              setPortfolioItems(reloadData.items || []);
            }
          }
          onNotification('Portfolio item saved successfully!', 'success');
        }
      } else {
        onNotification('Failed to save portfolio item', 'error');
      }
    } catch (error) {
      console.error('Error saving portfolio item:', error);
      onNotification('Failed to save portfolio item', 'error');
    }
    
    setEditingItem(null);
  };

  const handleDeleteItem = async (id: string) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.PORTFOLIO}/${id}`, {
        method: 'DELETE',
        credentials: 'include', // Include session cookie
      });

      if (response.ok) {
        // Reload items from server
        const reloadResponse = await fetch(API_ENDPOINTS.PORTFOLIO, {
          credentials: 'include',
        });
        if (reloadResponse.ok) {
          const reloadData = await reloadResponse.json();
          if (reloadData.success) {
            setPortfolioItems(reloadData.items || []);
          }
        }
        onNotification('Portfolio item deleted successfully', 'success');
      } else {
        onNotification('Failed to delete portfolio item', 'error');
      }
    } catch (error) {
      console.error('Error deleting portfolio item:', error);
      onNotification('Failed to delete portfolio item', 'error');
    }
  };

  const handleMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        setUploadingMedia(file.name);
        
        const formData = new FormData();
        formData.append('image', file);
        formData.append('title', editingItem?.title || 'Untitled');
        formData.append('description', editingItem?.description || '');
        formData.append('category', editingItem?.category || 'architectural');
        formData.append('featured', editingItem?.featured ? 'true' : 'false');
        formData.append('order', editingItem?.order?.toString() || '1');

        const response = await fetch(API_ENDPOINTS.UPLOAD_PORTFOLIO, {
          method: 'POST',
          body: formData,
          credentials: 'include', // Include session cookie
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const result = await response.json();
        
        const mediaItem: MediaItem = {
          id: Date.now().toString() + Math.random(),
          type: result.item.type || (file.type.startsWith('video/') ? 'video' : 'image'),
          url: result.item.mediaUrl || result.item.imageUrl,
          filename: result.item.filename || file.name,
          isPrimary: editingItem?.media.length === 0, // First uploaded item is primary
          caption: '',
        };

        return mediaItem;
      });

      const uploadedMedia = await Promise.all(uploadPromises);
      
      if (editingItem) {
        setEditingItem(prev => prev ? { 
          ...prev, 
          media: [...prev.media, ...uploadedMedia] 
        } : null);
      }
      onNotification(`${files.length} media file(s) uploaded successfully!`, 'success');
    } catch (error) {
      onNotification('Failed to upload media files', 'error');
    } finally {
      setUploading(false);
      setUploadingMedia(null);
    }
  };

  const handleRemoveMedia = (mediaId: string) => {
    if (editingItem) {
      const updatedMedia = editingItem.media.filter(media => media.id !== mediaId);
      // If we removed the primary media, make the first remaining one primary
      if (updatedMedia.length > 0 && !updatedMedia.some(media => media.isPrimary)) {
        updatedMedia[0].isPrimary = true;
      }
      setEditingItem(prev => prev ? { ...prev, media: updatedMedia } : null);
    }
  };

  const handleSetPrimaryMedia = (mediaId: string) => {
    if (editingItem) {
      const updatedMedia = editingItem.media.map(media => ({
        ...media,
        isPrimary: media.id === mediaId
      }));
      setEditingItem(prev => prev ? { ...prev, media: updatedMedia } : null);
    }
  };

  const handleUpdateMediaCaption = (mediaId: string, caption: string) => {
    if (editingItem) {
      const updatedMedia = editingItem.media.map(media => 
        media.id === mediaId ? { ...media, caption } : media
      );
      setEditingItem(prev => prev ? { ...prev, media: updatedMedia } : null);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'architectural': return 'primary';
      case 'mechanical': return 'secondary';
      case 'artistic': return 'success';
      case 'functional': return 'warning';
      default: return 'default';
    }
  };

  const toggleFeatured = (id: string) => {
    setPortfolioItems(prev => prev.map(item => 
      item.id === id ? { ...item, featured: !item.featured } : item
    ));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" color="primary" sx={{ fontWeight: 600 }}>
          Portfolio Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddItem}
        >
          Add Portfolio Item
        </Button>
      </Box>

      <Grid container spacing={3}>
        {portfolioItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
                border: item.featured ? `2px solid ${theme.palette.primary.main}` : 'none',
              }}
            >
              <CardMedia
                component={item.media.length > 0 && item.media[0].type === 'video' ? 'video' : 'img'}
                height="200"
                image={item.media.length > 0 ? item.media[0].url : '/placeholder-image.jpg'}
                alt={item.title}
                sx={{ cursor: 'pointer' }}
                onClick={() => setPreviewMedia(item.media.length > 0 ? item.media[0] : null)}
                controls={item.media.length > 0 && item.media[0].type === 'video'}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {item.title}
                  </Typography>
                  {item.featured && (
                    <Chip label="Featured" color="primary" size="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {item.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={item.category}
                    color={getCategoryColor(item.category) as any}
                    size="small"
                  />
                  <Chip
                    label={`Order: ${item.order}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </CardContent>
              <CardActions>
                <IconButton
                  size="small"
                  onClick={() => setPreviewMedia(item.media.length > 0 ? item.media[0] : null)}
                  color="primary"
                >
                  <Visibility />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleEditItem(item)}
                  color="secondary"
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => toggleFeatured(item.id)}
                  color={item.featured ? 'warning' : 'default'}
                >
                  ⭐
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteItem(item.id)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {portfolioItems.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No portfolio items yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Add Portfolio Item" to get started
          </Typography>
        </Box>
      )}

      {/* Edit/Add Dialog */}
      <Dialog open={!!editingItem} onClose={() => setEditingItem(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem?.id === Date.now().toString() ? 'Add Portfolio Item' : 'Edit Portfolio Item'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={editingItem?.title || ''}
              onChange={(e) => setEditingItem(prev => prev ? { ...prev, title: e.target.value } : null)}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={editingItem?.description || ''}
              onChange={(e) => setEditingItem(prev => prev ? { ...prev, description: e.target.value } : null)}
              multiline
              rows={4}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={editingItem?.category || 'architectural'}
                onChange={(e) => setEditingItem(prev => prev ? { ...prev, category: e.target.value as any } : null)}
                label="Category"
              >
                <MenuItem value="architectural">Architectural</MenuItem>
                <MenuItem value="mechanical">Mechanical</MenuItem>
                <MenuItem value="artistic">Artistic</MenuItem>
                <MenuItem value="functional">Functional</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Media Files (Images & Videos)
              </Typography>
              
              {/* Media Upload */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                  sx={{ mb: 2 }}
                >
                  {uploading ? <CircularProgress size={20} /> : 'Upload Media Files'}
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleMediaUpload}
                    style={{ display: 'none' }}
                  />
                </Button>
                {uploadingMedia && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Uploading: {uploadingMedia}
                  </Typography>
                )}
              </Box>

              {/* Media Gallery */}
              {editingItem?.media && editingItem.media.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Uploaded Media ({editingItem.media.length} files)
                  </Typography>
                  <Grid container spacing={2}>
                    {editingItem.media.map((media, index) => (
                      <Grid item xs={6} sm={4} md={3} key={media.id}>
                        <Card sx={{ position: 'relative' }}>
                          <CardMedia
                            component={media.type === 'video' ? 'video' : 'img'}
                            height="120"
                            image={media.url}
                            alt={media.filename}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setPreviewMedia(media)}
                            controls={media.type === 'video'}
                          />
                          <CardContent sx={{ p: 1 }}>
                            <Typography variant="caption" noWrap>
                              {media.filename}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleSetPrimaryMedia(media.id)}
                                color={media.isPrimary ? 'primary' : 'default'}
                                title="Set as primary"
                              >
                                ⭐
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveMedia(media.id)}
                                color="error"
                                title="Remove"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Caption (optional)"
                              value={media.caption || ''}
                              onChange={(e) => handleUpdateMediaCaption(media.id, e.target.value)}
                              sx={{ mt: 1 }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Order"
                type="number"
                value={editingItem?.order || 1}
                onChange={(e) => setEditingItem(prev => prev ? { ...prev, order: parseInt(e.target.value) || 1 } : null)}
                sx={{ width: 120 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <input
                  type="checkbox"
                  checked={editingItem?.featured || false}
                  onChange={(e) => setEditingItem(prev => prev ? { ...prev, featured: e.target.checked } : null)}
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Featured Item
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingItem(null)}>Cancel</Button>
          <Button onClick={handleSaveItem} variant="contained">
            {editingItem?.id === Date.now().toString() ? 'Add Item' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewMedia} onClose={() => setPreviewMedia(null)} maxWidth="md" fullWidth>
        <DialogContent>
          {previewMedia && (
            <>
              {previewMedia.type === 'video' ? (
                <video
                  src={previewMedia.url}
                  controls
                  style={{ width: '100%', height: 'auto' }}
                />
              ) : (
                <img
                  src={previewMedia.url}
                  alt={previewMedia.filename}
                  style={{ width: '100%', height: 'auto' }}
                />
              )}
              {previewMedia.caption && (
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                  {previewMedia.caption}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PortfolioManagement;
